/**
 * Market intelligence console - English.
 *
 * This file is NOT optional even though every call site passes a defaultValue. i18n is
 * configured with `fallbackLng: 'ar'`, so a key missing from `en` resolves to the ARABIC
 * string rather than to the default. An English reader would get Arabic copy.
 *
 * Wording rules these strings are bound by (CONTEXT.md §8.5, enforced by tests):
 *   - three neutral positions only: within / above most registered deals / below most.
 *     Never "overpriced", never "a bargain", never "a deal".
 *   - gross yield is always labelled gross, before costs and the 5% transaction tax.
 *   - sample size and period sit next to the figure, not in small print.
 *   - no em dashes or en dashes in user-facing copy (THEME.md §2.1d). Plain hyphen.
 */

const marketConsole = {
  common: {
    source: 'Ministry of Justice registered sale deeds',
    disclaimer: 'A statistical guide, not a property valuation',
    how: 'How this works',
    loading: 'Loading market data',
    none: 'Nothing to show',
    sar: 'SAR',
    m2: 'm²',
    perM2: 'SAR/m²',
    retry: 'Try again',
    adminOnly: 'Administrators',
  },

  // Every card covers a different stretch of history. Showing only the newest quarter in
  // the page footer made them all look like they were about that one quarter.
  period: {
    measuredOver: 'Measured over',
    comparedWith: 'Compared with',
    upTo: 'All registered deeds up to',
    rentUpTo: 'Rent data up to',
    pricesFrom: 'Registered prices from',
    since: 'First appearance from',
    thisQuarter: 'Latest quarter',
    forecastFor: 'Forecast for',
    published: 'Regulator published',
  },

  masthead: {
    eyebrow: 'Registered market',
    title: 'Market intelligence',
    subtitle:
      'Everything the registered deeds can answer, measured rather than asserted. Every figure is a range, every range carries what it was built from, and anything we cannot answer honestly says so.',
    districts: 'districts priced',
    rentDistricts: 'districts with rent',
    deeds: 'registered deeds',
    shortlist: 'districts shortlisted',
  },

  nav: {
    a: 'Your property',
    b: 'This district',
    c: 'Choosing where',
    d: 'Buy or rent',
    e: 'Follow the money',
    f: 'How we check ourselves',
  },

  group: {
    a: 'What one property is worth, what it rents for, and how size moves it.',
    b: 'Four readings of one place: how solid its record is, how built out it is, how busy next quarter looks, and what a nearby station goes with.',
    c: 'Where a budget reaches, what looks like somewhere cheaper, and when to sell.',
    d: 'Gross yield only, before service charges, vacancy, maintenance and the 5% transaction tax. None of those are in this data and none are assumed.',
    e: 'Where value moved, what villas sell for, and where to look for land.',
    f: 'What we deliberately refuse to report on, and why. Anyone can download the same government files; almost nobody publishes their own exclusions.',
  },

  place: {
    city: 'City',
    district: 'District',
    pickCity: 'Choose a city',
    pickCityFirst: 'Choose a city first',
    allCities: 'All cities',
    search: 'Type to search',
    loading: 'Loading…',
    noMatch: 'No district matches that',
    nDistricts_one: '{{count}} district with enough deeds',
    nDistricts_other: '{{count}} districts with enough deeds',
  },

  segment: {
    res_land: 'Residential land',
    com_land: 'Commercial land',
    apartment: 'Apartment',
    agri_land: 'Agricultural land',
    other_built: 'Other built',
    ind_land: 'Industrial land',
  },

  rentType: {
    apartment: 'Apartment',
    villa: 'Villa',
    studio: 'Studio',
    floor: 'Floor',
    duplex: 'Duplex',
  },

  driver: {
    earlyStage: 'Still mostly land',
    gettingBusier: 'Getting busier',
    entryPrice: 'Cheaper than its city',
    tradeable: 'Trades often',
    dataSolid: 'Solid record',
  },

  confidence: {
    firm: 'Firm',
    firmN_one: 'Firm · {{count}} deed',
    firmN_other: 'Firm · {{count}} deeds',
    indicative: 'Indicative',
    indicativeN_one: 'Indicative · {{count}} deed',
    indicativeN_other: 'Indicative · {{count}} deeds',
    extrapolated: 'Beyond recorded sizes',
    contested: 'Disputed by the deeds',
  },

  rail: {
    aria: 'Estimated between {{low}} and {{high}}, centred on {{mid}}',
    legendModel: 'Estimated',
    legendMeasured: 'Registered here',
    legendMeasuredN_one: 'Registered here ({{count}} deed)',
    legendMeasuredN_other: 'Registered here ({{count}} deeds)',
    legendYours: 'Your figure',
    legendExtrapolated: 'Dashed: beyond what traded here',
    legendIndicative: 'Dashed: thin record',
    contested:
      'The estimate and this district’s own recent deeds disagree by more than double. Treat the recorded figure as the stronger of the two.',
  },

  verdict: {
    within: {
      title: 'Within the usual range',
      note: 'This asking price sits inside what deeds of this size registered at here.',
    },
    edge: {
      title: 'At the edge of the usual range',
      note: 'Just outside the range, by less than five per cent.',
    },
    above: {
      title: 'Above most registered deals',
      note: 'Higher than the top of what deeds of this size registered at here.',
    },
    below: {
      title: 'Below most registered deals',
      note: 'Lower than the bottom of what deeds of this size registered at here.',
    },
    yours: 'Your price',
    notActionable:
      'The record behind this district is thin, stretched beyond the sizes it trades, or disputed by its own deeds. Read the position as an observation, not a judgement.',
  },

  prov: {
    deeds_one: '{{count}} registered deed',
    deeds_other: '{{count}} registered deeds',
    typicalMiss: 'typically within {{pct}}%',
  },

  evidence: {
    toggle: 'How we know this',
    model: 'Model',
    trainedOn: 'Fitted on',
    testedOn: 'Tested on',
    typicalMiss: 'Typical miss',
    cover: 'Range held the truth',
    beat: 'Compared against',
    whyThis: 'Why this one',
    note: 'Note',
    versus: 'against',
    deedsN_one: '{{count}} registered deed',
    deedsN_other: '{{count}} registered deeds',
    ratesN_one: '{{count}} published rate',
    ratesN_other: '{{count}} published rates',
    districtsHeldOut_one: '{{count}} district it had never seen',
    districtsHeldOut_other: '{{count}} districts it had never seen',
  },

  curve: {
    aria: 'Price per square metre as the plot size grows',
    legendModel: 'Estimated per m²',
    legendMeasured: 'Registered here',
    legendHollow: 'Hollow: beyond the sizes it trades',
    axis: 'Across: plot size in m²',
  },

  reason: {
    estimateOff: {
      title: 'The price estimate is not loaded',
      body: 'The engine file is not on the server right now. Nothing else on this page is affected.',
    },
    rentOff: {
      title: 'The rent data is not loaded',
      body: 'The engine file is not on the server right now. Nothing else on this page is affected.',
    },
    opportunityOff: {
      title: 'The budget data is not loaded',
      body: 'The engine file is not on the server right now. Nothing else on this page is affected.',
    },
    insightsOff: {
      title: 'The deed readings are not loaded',
      body: 'The engine file is not on the server right now. Nothing else on this page is affected.',
    },
    forecastOff: {
      title: 'The forecast is not loaded',
      body: 'The engine file is not on the server right now. Nothing else on this page is affected.',
    },
    districtUnknown: {
      title: 'That district was not recognised',
      body: 'Pick one from the list. We answer about a district we can name, rather than guessing at the nearest one.',
    },
    notEnough: {
      title: 'Not enough registered deeds',
      body: 'There are too few deeds here to report a range honestly. The gate exists so a thin record cannot be dressed up as a firm figure.',
    },
    segmentUnsupported: {
      title: 'Not available for this property type',
      body: 'Ministry of Justice deeds only separate flats from land, so villas, floors and townhouses are all filed as land. A sale price for them would be a guess.',
    },
    segmentRequired: { title: 'Choose a property type', body: 'Pick a type to get an answer.' },
    areaRequired: {
      title: 'Enter an area',
      body: 'The answer depends on the size, so we need it.',
    },
    priceRequired: {
      title: 'Enter a price',
      body: 'Add an asking price to check it against the range.',
    },
    sizeOutside: {
      title: 'That size is outside anything recorded here',
      body: 'No deed near that size has ever registered in this district, so any figure would be invention rather than measurement.',
    },
    typeUnsupported: {
      title: 'No rent published for this type',
      body: 'REGA publishes rent for flats, villas, studios, floors and duplexes only.',
    },
    budgetRequired: { title: 'Enter a budget', body: 'Tell us what you have to spend.' },
    budgetTooLow: {
      title: 'Nothing matches that budget',
      body: 'Nowhere in the country registers an entry price that low for this type.',
    },
    noMetro: {
      title: 'No metro network in that city',
      body: 'Riyadh is the only city with a network in the data, so there is nothing to compare against elsewhere.',
    },
    reviewFailed: {
      title: 'The review could not run',
      body: 'Something went wrong reading that listing.',
    },
    requestFailed: {
      title: 'Could not reach the server',
      body: 'The request did not get through. Try again in a moment.',
    },
    forbidden: {
      title: 'Administrators only',
      body: 'This reading is a statement about the registry and belongs to whoever is judging how far to trust a number.',
    },
  },

  a1: {
    title: 'What is it worth?',
    tagline:
      'The range a property of this size registers at in this district, and where an asking price sits against it.',
    how: 'A model fitted on every usable registered sale deed estimates the price per square metre for your district, property type and size. The range around it is measured from how wrong the model actually was on a quarter it had never seen, banded by how many deeds the district has. Where the district registered deeds near your size, those are shown beside the estimate so you can see the two disagree or agree.',
    coverage: '{{districts}} districts',
    area: 'Area',
    areaHint: 'The plot or unit size',
    areaRequired: 'Enter the area in square metres',
    areaInvalid: 'That is not a usable area',
    price: 'Asking price (optional)',
    priceHint: 'Add one to see where it sits against the range',
    type: 'Property type',
    run: 'Show the range',
    idle: 'Pick a district and a size, and the range will be drawn here.',
    steps: [
      'Finding this district in the registered deeds',
      'Reading what changed hands near this size',
      'Running the estimate',
      'Measuring the range from held-out error',
    ],
    resultTitle: '{{area}} m² in {{district}}',
    totalTitle: 'What that size registers at',
    totalNote: 'An 80% range over registered deeds, not an offer or a valuation',
    perM2Title: 'Price per square metre',
    measuredLabel: 'Registered near this size',
    yourPrice: 'Your price',
    extrapolated:
      'This district trades between {{from}} and {{to}} m². Your size sits outside that, so the figure is an extension of the curve rather than something measured.',
  },

  a2: {
    title: 'Does a bigger plot cost more per metre?',
    tagline:
      'Almost everywhere the price per square metre falls as the plot grows. This shows by how much, here.',
    how: 'The same model is asked the same question at every size on this property type’s published ladder. Where the district registered deeds near a rung, the recorded median is drawn beside the estimate. A hollow point means no deed near that size has ever registered here.',
    idle: 'Pick a district to draw its curve.',
    step1: 'Reading this district’s size ladder',
    step2: 'Estimating each rung',
    step3: 'Matching against recorded deeds',
    resultTitle: '{{district}}',
    falls:
      'Going from {{from}} to {{to}} m² here, the price per square metre falls about {{pct}}%.',
    rises: 'Unusually, the price per square metre rises about {{pct}}% as the plot grows here.',
    flat: 'The price per square metre is roughly flat across sizes here.',
  },

  a3: {
    title: 'What does it rent for?',
    tagline:
      'REGA’s published rent where it exists, and an estimate where it does not. The two are never mixed up.',
    how: 'Where the regulator published a rate for this district and type, that figure is passed through untouched with the number of contracts behind it. Where it published nothing, a model estimates the rate and shows a range instead of a single number. A published rate is never adjusted by the model.',
    coverage: '{{districts}} districts',
    type: 'Property type',
    area: 'Area (optional)',
    areaHint: 'Add one to get a monthly figure',
    idle: 'Pick a district to see what it rents for.',
    step1: 'Looking for a published rate',
    step2: 'Falling back to the estimate if there is none',
    published_one: 'Published by REGA · {{count}} contract',
    published_other: 'Published by REGA · {{count}} contracts',
    estimated: 'Estimated - REGA published nothing here',
    perM2: 'Rent per m² per year',
    monthly: 'Monthly for {{area}} m²',
    estimateCaveat:
      'REGA publishes nothing for this district and type, so this is an estimate with a range around it. Where the regulator did publish, we quote the regulator.',
  },

  b: {
    title: 'Reading the district',
    tagline:
      'Four things the registered deeds say about one place: how solid its record is, how far through being built it is, what a nearby station goes with, and how busy next quarter looks.',
    how: 'Every one of these is arithmetic over the deeds, not an opinion. Risk measures the evidence, not the place: a thin record is a thin record, and says nothing about whether somewhere is good. The forecast is the one model here, and it predicts deed volume, never price.',
    idle: 'Pick a district to read what its deeds say.',
    step1: 'Scoring the strength of its record',
    step2: 'Reading what share of deeds is still land',
    step3: 'Measuring distance to the nearest station',
    step4: 'Forecasting next quarter’s deed volume',
    risk: {
      title: 'How much weight its record carries',
      hint: 'This reads the evidence, not the place. A district can be excellent and still have a thin record.',
      clean: 'Nothing about this record weakens it.',
      deeds: 'Deeds',
      perQuarter: 'Per quarter',
      quarters: 'Active quarters',
      topDeal: 'Biggest single deal',
      band: { low: 'Solid', medium: 'Mixed', high: 'Thin' },
    },
    stage: {
      title: 'How far through being built',
      note: '{{built}} of recent deeds here are built property and {{land}} are still land.',
      step: {
        land: 'Land',
        breaking_ground: 'Breaking ground',
        building: 'Building',
        built_out: 'Built out',
      },
    },
    forecast: {
      title: 'How busy next quarter looks',
      hint: 'This forecasts how many deeds register, never what they register at.',
      now: 'This quarter',
      normal: 'Its own normal',
      deeds: 'deeds',
      reverting:
        'This is the district coming back to its own usual level after an odd quarter, not breaking above it.',
      above:
        'Forecast above its own four-quarter normal. Only 26 districts in the country qualify.',
      steady: 'Forecast to stay close to its own usual level.',
      unconfident:
        'The range still straddles this quarter’s count, so the direction has not been earned. Read it as "about the same".',
    },
    metro: {
      title: 'Being near a metro station',
      district: 'This district sits {{km}} km from the nearest station, in the {{band}} band.',
      reference: 'baseline',
      deeds_one: '{{count}} deed',
      deeds_other: '{{count}} deeds',
      caveat: 'Observational - metro lines are built where demand already is.',
    },
  },

  c1: {
    title: 'What will my budget buy?',
    tagline:
      'Districts where something at your budget actually changed hands. Registered prices, not asking prices.',
    how: 'Your budget is matched against each district’s entry price - the bottom of the middle half of what registered there, so a quarter of deals went below it. Matching on the median instead would hide every district where the cheap end is within reach.',
    coverage: '{{n}} districts',
    budget: 'Your budget',
    budgetRequired: 'Enter a budget in riyals',
    city: 'Narrow to a city (optional)',
    sort: 'Order by',
    sortHeadroom: 'Most room to spare',
    sortPrice: 'Cheapest first',
    sortLiquidity: 'Trades most often',
    sortDeeds: 'Strongest record',
    run: 'Find districts',
    idle: 'Enter a budget to see where it reaches.',
    step1: 'Reading registered prices by district',
    step2: 'Taking each district’s entry price',
    step3: 'Matching your budget against them',
    resultTitle: '{{total}} districts across {{cities}} cities',
    comfortable_one: '{{count}} where even the typical deal fits',
    comfortable_other: '{{count}} where even the typical deal fits',
    cheapestTitle: 'The cheapest entry price anywhere',
    caption: 'Districts within budget',
    colDistrict: 'District',
    colEntry: 'Entry price',
    colMedian: 'Typical',
    colArea: 'Typical size',
    colHeadroom: 'Room to spare',
    colDeeds: 'Deeds',
    provisional:
      'Some of these districts still carry provisional deeds, so their figures may move.',
  },

  c4: {
    title: 'Districts that are new to the register',
    tagline:
      'Places that started registering deeds in the last two years, where nothing registered before.',
    how: 'A district counts as new when its first registered deed falls inside the window and it has since built up enough deeds to be worth naming. It says a place began trading, not that it is a good place to buy.',
    city: 'City',
    step1: 'Looking for first appearances',
    since_one: '{{count}} district first registered a deed since {{from}}',
    since_other: '{{count}} districts first registered a deed since {{from}}',
    legend: 'The figure beside each name is its deed count.',
  },

  d: {
    published: 'published',
    estimated: 'estimated',
    publishedTitle: 'Rent published by REGA',
    estimatedTitle: 'Rent estimated - REGA published nothing',
  },

  d1: {
    title: 'Where the rent goes furthest',
    tagline:
      'Gross yield by district, built only where both sides of the sum are solid: published rent and registered sale prices.',
    how: 'A district qualifies only when REGA published its rent from at least twenty contracts AND at least twenty sale deeds registered there. Both gates have to pass, which is why there are hundreds of rows here rather than thousands. Yield is annual rent divided by price, before any cost.',
    coverage: '{{n}} districts',
    type: 'Property type',
    allTypes: 'All types',
    minYield: 'Minimum yield',
    minYieldHint: 'Per cent, e.g. 6',
    any: 'Any',
    step1: 'Pairing published rent with registered prices',
    step2: 'Dropping districts where either side is thin',
    resultTitle_one: '{{count}} district',
    resultTitle_other: '{{count}} districts',
    caption: 'Gross yield by district',
    colDistrict: 'District',
    colYield: 'Gross yield',
    colYears: 'Years of rent',
    colRent: 'Monthly rent',
    colPrice: 'Typical price',
    colEvidence: 'Behind it',
    contracts_one: '{{count}} contract',
    contracts_other: '{{count}} contracts',
    deeds_one: '{{count}} deed',
    deeds_other: '{{count}} deeds',
  },

  d2: {
    title: 'Rent it or buy it?',
    tagline:
      'How many years of rent it takes to equal the price. Pick a district, or leave it blank for the national picture.',
    how: 'Annual rent against registered sale price, both per square metre so the sizes cancel. Where REGA published no rent for a district, a modelled rate is used and the row says so - about a third of the national list rests on one.',
    type: 'Property type (optional)',
    allTypes: 'Any type',
    step1: 'Comparing annual rent against price',
    yearsTitle: 'Years of rent that equal the price',
    grossYield: 'A gross yield of {{pct}}, before any cost',
    monthlyRent: 'Typical monthly rent',
    price: 'Typical price',
    area: 'Typical size',
    evidence: 'Behind it',
    restsOnEstimate:
      'The rent side of this sum is a modelled estimate, because REGA published nothing for this district and type.',
    listTitle_one: '{{count}} district',
    listTitle_other: '{{count}} districts',
    restingCount_one:
      '{{count}} of these rests on an estimated rent rather than a published one',
    restingCount_other:
      '{{count}} of these rest on an estimated rent rather than a published one',
    caption: 'Years of rent by district',
    colDistrict: 'District',
    colYears: 'Years of rent',
    colYield: 'Gross yield',
    colRent: 'Monthly rent',
    colPrice: 'Typical price',
  },

  e1: {
    title: 'Where the money moved',
    tagline:
      'The total value registered over the last four quarters against the four before it.',
    how: 'Every registered sale price is added up per place across four quarters and compared with the four before. Places whose move is explained by a change in how the feed publishes are shown separately rather than mixed in, because that is a publishing change and not a market.',
    byCity: 'By city',
    byDistrict: 'By district',
    step1: 'Summing registered value per place',
    step2: 'Setting aside places with a feed break',
    window: '{{last}} against {{prev}}',
    truncated:
      'This is the {{count}} biggest movers the engine publishes, not a complete ranking of the country. Do not read a place missing from it as a place that did not move.',
    caption: 'Change in registered value',
    colName: 'Place',
    colChange: 'Change in value',
    colValue: 'Value registered',
    colDeeds: 'Deeds',
    colFlags: 'Watch for',
    oneDeal: 'one deal',
    provisional: 'provisional',
    affectedTitle: 'Explained by a change in the feed',
    affectedNote:
      'These moved because of how the registry publishes, not because the market did something. They are kept here rather than hidden, and kept out of the table above rather than mixed in.',
    breakAt: 'feed changed {{period}}',
  },

  e2: {
    title: 'What villas sell for',
    tagline:
      'The regulator’s own published villa indicator. The only place a villa sale price can honestly come from.',
    how: 'Ministry of Justice deeds only separate flats from land, so villas, floors and townhouses are all filed as land. Relabelling them would bury a guess inside the dataset where nobody would ever find it, so we do not. Villa prices come from REGA’s published indicators or they do not appear at all - which is why the estimate tool further up will not price a villa.',
    district: 'District (optional)',
    cityFallbackHint:
      'Choose a city alone for the city figure, or add a district for its own if REGA published one.',
    idle: 'Pick a city to see its published villa figure.',
    step1: 'Looking for a published villa indicator',
    levelDistrict: 'This district',
    levelCity: 'City figure',
    ppm: 'Per m² · {{period}}',
    deals_one: 'from {{count}} published deal',
    deals_other: 'from {{count}} published deals',
    vsTypical: '{{pct}} against the national typical',
    history: 'What REGA published before',
    caveat:
      'This is REGA’s published figure, quoted and never recomputed. It is not comparable with the deed-based ranges elsewhere on this page, which come from a different register.',
  },

  e3: {
    title: 'Where to look for land',
    tagline:
      'A shortlist with its reasons, and its objections. Every driver is something already measured, not a forecast of price.',
    how: 'Five readings are taken for every district that has a usable record, and a district reaches the shortlist when at least three of them fire. Each row carries what argues for it and what argues against it - the objections are never removed by any filter, because a shortlist without them is advertising.',
    coverage: '{{n}} districts',
    driver: 'Must have',
    anyDriver: 'Any reason',
    step1: 'Reading five measurements per district',
    step2: 'Dropping districts whose feed changed level',
    step3: 'Collecting the objections',
    resultTitle_one: '{{count}} district',
    resultTitle_other: '{{count}} districts',
    withWarning_one: '{{count}} carries an objection',
    withWarning_other: '{{count}} carry an objection',
    vsCity: '{{pct}} vs city',
    reasons: 'Reasons for and against',
    for: 'For',
    against: 'Against',
    noneAgainst: 'Nothing recorded against it',
    excludedNote:
      '{{count}} more districts were left out because their feed changed level. They are listed further down.',
  },

  f1: {
    title: 'Districts we refuse to report on',
    tagline:
      'Places whose feed changed level, so what they publish is a filing arrangement rather than a market.',
    how: 'When a district’s deed count shifts so sharply that a publishing change explains it better than a market does, it is taken out of the shortlist entirely rather than quietly carried with a caveat nobody reads. Listing them here is the point: you can see exactly what was left out.',
    filter: 'Find a district',
    filterHint: 'Leave blank to see the first 150',
    filterPlaceholder: 'District or city',
    step1: 'Listing the exclusions',
    count_one: '{{count}} district excluded',
    count_other: '{{count}} districts excluded',
    noMatch: 'Nothing matches that',
  },

  f2: {
    title: 'When the data changed, not the market',
    tagline:
      'Series whose level shifted so sharply that a change in how the registry publishes explains it better than buyers and sellers do.',
    how: 'A sharp step in a series is tested against how many other series broke in the same quarter. One place moving on its own is a market; nine moving together in one quarter is a filing change. The clearest example is Riyadh, which moved to the Real Estate Registry in 2025-Q2 and shows up as the biggest faller in the country on paper.',
    show: 'Show',
    onlyFeed: 'Only likely filing changes',
    allBreaks: 'Every break',
    step1: 'Testing each series for a step',
    step2: 'Counting how many broke together',
    caption: 'Detected level changes',
    colName: 'Series',
    colShift: 'Level change',
    colBeforeAfter: 'Before / after',
    colCause: 'Likelier cause',
    colTogether: 'Broke together',
    before: 'before',
    after: 'after',
    feed: 'the feed',
    market: 'the market',
    level: { city: 'City', district: 'District' },
    caveat:
      'A break does not mean a number is wrong. It means the series either side of it is not measuring the same thing, so comparing across it would mislead.',
  },

  f3: {
    title: 'Prices that were set, not agreed',
    tagline:
      'District-quarters where one exact rate per m² repeats far too often to be a market.',
    how: 'This is a statement about the registry, not about a district, which is why it is not public. It matters because these deeds sit inside the same pool every price band on the site is built from, so knowing where they concentrate tells you how far to trust a given number.',
    step1: 'Looking for repeated rates',
    summary: '{{groups}} district-quarters covering {{deeds}} deeds',
    caption: 'Administered price groups',
    colDistrict: 'District',
    colRate: 'The repeated rate',
    colShare: 'Share of the quarter',
    colDeeds: 'Deeds at that rate',
    colShape: 'Shape',
  },

  f4: {
    title: 'Listings worth a second look',
    tagline:
      'Duplicate deed numbers, expired licences, impossible prices. Observations for a reviewer, never an automatic action.',
    how: 'The most recent live listings are checked against each other and against the registered market. Nothing here is a finding of fraud and nothing is hidden or deleted automatically. A price is only ever flagged when the district’s record is firm, within the sizes it trades, and not disputed by its own deeds.',
    severity: 'Show',
    highOnly: 'Serious only',
    anyFlag: 'Anything flagged',
    step1: 'Reading the most recent live listings',
    step2: 'Checking deed and licence numbers for repeats',
    step3: 'Testing prices against the registered range',
    resultTitle: '{{total}} flagged of {{scanned}} scanned',
    capped:
      'The sweep stopped at {{max}} listings, so this is a partial view rather than the whole board.',
    clean: 'Nothing flagged in the recent listings.',
    noPriceCheck: 'No price check was possible here ({{reason}}).',
  },

  models: {
    title: 'The models behind this page',
    blurb:
      'Three of the instruments use a model. Each one was chosen by running eighteen candidates over the same folds with the same guards, and each is measured against the simple rule it replaces. Everything else on this page is arithmetic over the deeds.',
    estimate: 'Price per m² for a size',
    rent: 'Rent where REGA published nothing',
    forecast: 'Next quarter’s deed volume',
    miss: 'typically within {{a}}%, against {{b}}% for what it replaced',
    notValuation:
      'None of this is a valuation. Property valuation is a licensed activity in Saudi Arabia, and these deeds record price, area, district and date and nothing about bedrooms, age or condition. Every figure here is a range over what was registered.',
  },

  // The preview: what someone without market access sees. Real figures for one example,
  // with the inputs fixed (pages/market-tools/MarketDemoContext.tsx).
  demo: {
    exampleTag: 'Example',
    locked: 'Example inputs, fixed in the preview',
    forbidden: {
      title: 'This result is locked',
      body: 'Changing the city, district or inputs needs a qualifying plan or the Market intelligence add-on.',
    },
    capped: 'The preview shows the first {{shown}} of {{total}}.',
  },

  // When there is no preview to offer, or the access check fails.
  access: {
    lockedTitle: 'Market intelligence is for subscribers',
    lockedBody:
      'Registered deal prices and rents for every district come with a qualifying plan or the Market intelligence add-on.',
    checkFailedTitle: "We couldn't check your access",
    checkFailedBody: 'Check your internet connection and try again.',
    // The card under the faded preview, for a visitor who is not signed in.
    previewSignInTitle: 'Sign in to keep exploring Market intelligence',
    previewSignInBody:
      'What you see above is one real example. Sign in or create an account to choose your own city and district and use every tool, with a qualifying plan or the Market intelligence add-on.',
  },
};

export default marketConsole;
